import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import { QUERY_KEYS } from '@/config/api'
import { publicAxios } from '@/lib/axios'
import { handleApiError, MutationConfig, queryClient } from '@/lib/react-query'
import { useAppStore } from '@/stores/appStore'
import { PublicDto } from '@/types'

const verifyUser = (data: PublicDto['VerifyUserCommand']): Promise<PublicDto['UserTokenDto']> => {
  return publicAxios.post('/auth/verify', data)
}

type UseVerifyUserOptions = {
  config?: MutationConfig<typeof verifyUser>
}

export const useVerifyUser = ({ config }: UseVerifyUserOptions = {}) => {
  const setTokens = useAppStore((state) => state.actions.setTokens)

  return useMutation({
    ...config,
    mutationFn: verifyUser,
    onSuccess: (data, vars, onRes, ctx) => {
      // Automatically set tokens
      setTokens({
        idToken: data.idToken,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresIn: data.expiresIn ?? 3600, // Default to 1h if not provided
      })
      // Drop the cached profile: the observer is still disabled here, so an invalidation would be a no-op.
      queryClient.removeQueries({ queryKey: [QUERY_KEYS.getProfile] })
      config?.onSuccess?.(data, vars, onRes, ctx)
    },
    onError: (error: AxiosError<PublicDto['ExceptionDetails']>, vars, onRes, ctx) => {
      handleApiError(error)
      config?.onError?.(error, vars, onRes, ctx)
    },
  })
}
